import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Field, change, stopAsyncValidation } from 'redux-form';
import { t } from 'i18next';
import { isRequired, maxAddressLine1, maxChar100 } from '../../../utils/validator';
import zipPlus4ToZip from './helpers';
import { capitalizeFirstLetter } from '../../../utils/func';
import AddressInput from '../../../lib/ui/form_elements/AddressInput';

function loadScriptAsync(uri, elementID) {
  return new Promise((resolve) => {
    const tag = document.createElement('script');
    tag.id = elementID;
    tag.src = uri;
    tag.async = true;
    tag.onload = () => {
      resolve();
    };

    tag.onerror = () => {
      document.getElementById(elementID).parentNode.removeChild(tag);
      loadScriptAsync(uri, elementID);
    };

    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
  });
}

function loadStyleSheetAsync(href, elementID) {
  return new Promise((resolve) => {
    const tag = document.createElement('link');
    tag.id = elementID;
    tag.rel = 'styleSheet';
    tag.href = href;
    tag.type = 'text/css';
    tag.async = true;
    tag.onload = () => {
      resolve();
    };

    tag.onerror = () => {
      document.getElementById(elementID).parentNode.removeChild(tag);
      loadStyleSheetAsync(href, elementID);
    };

    const firstLinkTag = document.getElementsByTagName('link')[0];
    firstLinkTag.parentNode.insertBefore(tag, firstLinkTag);
  });
}

class AddressComplete extends PureComponent {
  componentDidMount() {
    this.addressApiInit();
  }

  addressApiInit = async () => {
    if (!global.CPscriptLoaded) {
      global.CPscriptLoaded = loadScriptAsync('https://ws1.postescanada-canadapost.ca/js/addresscomplete-2.30.min.js', 'CPScript');
    }

    global.CPscriptLoaded.then(() => {
      if (!global.CPstyleLoaded) {
        global.CPstyleLoaded = loadStyleSheetAsync('https://ws1.postescanada-canadapost.ca/css/addresscomplete-2.30.min.css', 'CPStyle');
      }

      return global.CPstyleLoaded;
    }).then(() => {
      const {
        dispatch, formName, fieldName, contactType,
      } = this.props;

      const shipperAddressFields = [
        { element: fieldName, field: '', mode: global.pca.fieldMode.SEARCH },
      ];
      const options = {
        key: t('CanadaPostAddressCompleteApiKey'),
        countries: { codesList: 'CAN, USA' },
      };

      // Decorate field whose "name" and / or "id" matches shipperAddressFields elements' "element" attribute...
      const addressElement = new global.pca.Address(shipperAddressFields, options);

      // addressElement.onChange() =>
      addressElement.listen('populate', async (address) => {
        // Formatting pieces that will make up the field value, given country, ...
        let postalCode = '';
        let countryName = '';
        if (address.CountryIso2 === 'US') {
          postalCode = zipPlus4ToZip(address.PostalCode);
          countryName = address.CountryIso3;
        } else {
          postalCode = address.PostalCode;
          countryName = address.CountryName;
        }

        // Format field value...
        const formattedAddress = `${address.Line1}, ${address.City}, ${address.ProvinceCode} ${postalCode}, ${countryName}`;

        // Update redux form field value...
        dispatch(change(formName, `${contactType}Address`, formattedAddress));
        dispatch(change(formName, `${contactType}AddressLine1`, address.Line1));
        dispatch(change(formName, `${contactType}City`, address.City));
        dispatch(change(formName, `${contactType}ProvinceState`, address.ProvinceCode));
        dispatch(change(formName, `${contactType}PostalZipCode`, postalCode));
        dispatch(change(formName, `${contactType}Country`, countryName));

        dispatch(change(formName, `is${capitalizeFirstLetter(contactType)}AddressCanadaPost`, true));

        const { validator } = this.props;

        if (validator) {
          try {
            await validator();
          } catch (e) {
            dispatch(stopAsyncValidation(formName, e));
          }
        }
      });
    });
  };

  render() {
    const {
      dispatch,
      formName,
      fieldName,
      label,
      required,
      contactType,
      logInError,
    } = this.props;

    const isOptionalField = !required
      ? [maxChar100, maxAddressLine1]
      : [isRequired, maxChar100, maxAddressLine1];

    return (
      <Field
        id={fieldName}
        name={fieldName}
        label={label || t('Address')}
        component={AddressInput}
        validate={isOptionalField}
        maxLength={100}
        onChange={() => dispatch(change(formName, `is${capitalizeFirstLetter(contactType)}AddressCanadaPost`, false))}
        optional={!required}
        logInError={logInError}
      />
    );
  }
}

AddressComplete.propTypes = {
  dispatch: PropTypes.func,
  formName: PropTypes.string,
  contactType: PropTypes.string,
  fieldName: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  validator: PropTypes.func,
  logInError: PropTypes.shape({
    shipperAddress: PropTypes.string,
    consigneeAddress: PropTypes.string,
  }),
};

export default connect()(AddressComplete);
